import { Code, Server, Shield, Network, Settings } from 'lucide-react';
import BlogArticleLayout from '../components/BlogArticleLayout';

export default function PiHoleProxmoxBlogArticle({ darkMode = false }) {
  const article = {
    title: "PiHole auf Proxmox installieren: Schritt-für-Schritt Anleitung",
    excerpt: "Lernen Sie, wie Sie PiHole als Ubuntu Container auf Proxmox einrichten und Ihr Netzwerk vor Werbung und Trackern schützen.",
    author: "Dustin Derr",
    authorBio: "",
    date: "2025-06-04",
    readTime: "12 min",
    category: "Homelab",
    tags: ["PiHole", "Proxmox", "Ubuntu", "DNS", "Netzwerksicherheit", "Container"],
    image: "from-green-400 to-blue-600",
    views: 2841,
    likes: 167,
    comments: 34,
    tableOfContents: [
      { id: "introduction", title: "Was ist PiHole und warum Proxmox?" },
      { id: "prerequisites", title: "Voraussetzungen und Vorbereitung" },
      { id: "container-setup", title: "Ubuntu Container erstellen" },
      { id: "pihole-installation", title: "PiHole Installation" },
      { id: "network-configuration", title: "Netzwerk-Konfiguration" },
      { id: "advanced-settings", title: "Erweiterte Einstellungen" },
      { id: "maintenance", title: "Wartung und Updates" },
      { id: "troubleshooting", title: "Troubleshooting" },
      { id: "conclusion", title: "Fazit und weiterführende Tipps" }
    ]
  };

  return (
    <BlogArticleLayout darkMode={darkMode} article={article}>

      <section id="introduction">
        <h2>Was ist Pi-Hole und warum Proxmox?</h2>
        <p>
          Pi-Hole ist ein netzwerkweiter Ad-Blocker, der als DNS-Sinkhole fungiert und unerwünschte Inhalte bereits auf DNS-Ebene blockiert. Ursprünglich für den Raspberry Pi entwickelt, lässt sich Pi-Hole hervorragend auf anderen Systemen betreiben - insbesondere in virtualisierten Umgebungen wie Proxmox.
        </p>
        <p>
          Proxmox VE (Virtual Environment) bietet dabei entscheidende Vorteile: Container sind ressourcenschonender als vollwertige VMs, lassen sich schnell erstellen und verwalten, und bieten trotzdem die Isolation, die für kritische Infrastrukturdienste wie DNS wichtig ist.
        </p>

        <div className="callout">
          <h3 className="font-bold mb-2 flex items-center">
            <Shield size={20} style={{display:'inline',verticalAlign:'middle',marginRight:8}} />
            Warum Pi-Hole?
          </h3>
          <ul>
            <li>• Netzwerkweite Werbeblocker für alle Geräte</li>
            <li>• Schutz vor Malware und Tracking</li>
            <li>• Detaillierte DNS-Anfrage-Statistiken</li>
            <li>• Kein Software-Installation auf Endgeräten nötig</li>
          </ul>
        </div>
      </section>

      <section id="prerequisites">
        <h2>Voraussetzungen und Vorbereitung</h2>
        <p>
          Bevor wir mit der Installation beginnen, stellen Sie sicher, dass folgende Voraussetzungen erfüllt sind:
        </p>

        <div className="grid-2">
          <div className="highlight-box">
            <h4 className="font-bold mb-2 text-green-600 flex items-center">
              <Server size={16} style={{display:'inline',verticalAlign:'middle',marginRight:8}} />
              Hardware Requirements
            </h4>
            <ul>
              <li>• Proxmox VE 7.0 oder höher</li>
              <li>• Mindestens 512 MB RAM für Container</li>
              <li>• 2 GB freier Speicherplatz</li>
              <li>• Statische IP-Adresse empfohlen</li>
            </ul>
          </div>
          <div className="highlight-box">
            <h4 className="font-bold mb-2 text-blue-600 flex items-center">
              <Network size={16} style={{display:'inline',verticalAlign:'middle',marginRight:8}} />
              Netzwerk Planning
            </h4>
            <ul>
              <li>• Freie IP-Adresse im Netzwerk</li>
              <li>• Zugriff auf Router-Einstellungen</li>
              <li>• SSH-Zugang zu Proxmox</li>
              <li>• Administrative Rechte</li>
            </ul>
          </div>
        </div>

        <div className="callout">
          <h4 className="font-bold mb-2">⚠️ Wichtiger Hinweis</h4>
          <p>
            Da Pi-Hole als DNS-Server fungiert, kann eine fehlerhafte Konfiguration den Internetzugang beeinträchtigen.
            Halten Sie die ursprünglichen DNS-Einstellungen bereit und testen Sie die Konfiguration schrittweise.
          </p>
        </div>
      </section>

      <section id="container-setup">
        <h2>Ubuntu Container erstellen</h2>
        <p>
          Wir beginnen mit der Erstellung eines Ubuntu LXC-Containers in Proxmox. LXC-Container sind ideal für Pi-Hole, da sie weniger Ressourcen verbrauchen als VMs, aber dennoch die nötige Isolation bieten.
        </p>

        <div className="highlight-box">
          <h4 className="font-bold mb-3 flex items-center">
            <Code size={16} style={{display:'inline',verticalAlign:'middle',marginRight:8}} />
            Schritt 1: Container Template herunterladen
          </h4>
          <div className="bg-white p-3 rounded border font-mono text-sm">
            <div>1. Datacenter → [Node] → local → CT Templates</div>
            <div>2. "Templates" klicken</div>
            <div>3. Ubuntu 22.04 Standard auswählen und herunterladen</div>
          </div>
        </div>

        <div className="highlight-box">
          <h4 className="font-bold mb-3">Schritt 2: Container konfigurieren</h4>
          <div>
            <strong>General:</strong>
            <ul className="ml-4">
              <li>• Node: Ihr Proxmox Node</li>
              <li>• CT ID: z.B. 101</li>
              <li>• Hostname: pihole</li>
              <li>• Password: Sicheres Passwort setzen</li>
            </ul>
          </div>
          <div>
            <strong>Resources:</strong>
            <ul className="ml-4">
              <li>• Memory: 1024 MB</li>
              <li>• Swap: 512 MB</li>
              <li>• Disk Size: 8 GB</li>
              <li>• CPU Cores: 1</li>
            </ul>
          </div>
          <div>
            <strong>Network:</strong>
            <ul className="ml-4">
              <li>• Bridge: vmbr0</li>
              <li>• Static IP: z.B. 192.168.1.100/24</li>
              <li>• Gateway: Ihre Router-IP</li>
            </ul>
          </div>
        </div>
      </section>

      <section id="pihole-installation">
        <h2>Pi-Hole Installation</h2>
        <p>
          Nach dem Start des Containers können wir mit der Pi-Hole Installation fortfahren.
        </p>

        <div className="highlight-box">
          <h4 className="font-bold mb-3">Schritt 1: Container starten und verbinden</h4>
          <div className="bg-white p-3 rounded border font-mono text-sm">
            <div>ssh root@192.168.1.100</div>
          </div>
        </div>

        <div className="highlight-box">
          <h4 className="font-bold mb-3">Schritt 2: System aktualisieren</h4>
          <div className="bg-white p-3 rounded border font-mono text-sm">
            <div>apt update && apt upgrade -y</div>
            <div>apt install curl -y</div>
          </div>
        </div>

        <div className="highlight-box">
          <h4 className="font-bold mb-3">Schritt 3: Pi-Hole installieren</h4>
          <div className="bg-white p-3 rounded border font-mono text-sm">
            <div>curl -sSL https://install.pi-hole.net | bash</div>
          </div>
        </div>
      </section>

      <section id="network-configuration">
        <h2>Netzwerk-Konfiguration</h2>
        <p>
          Um Pi-Hole netzwerkweit zu nutzen, müssen wir die DNS-Einstellungen anpassen.
        </p>

        <div className="grid-2">
          <div className="highlight-box">
            <h4 className="font-bold mb-2 text-blue-600">🏠 Router-Konfiguration</h4>
            <ul>
              <li>• Primärer DNS: 192.168.1.100</li>
              <li>• Sekundärer DNS: 1.1.1.1</li>
              <li>• Wirkt für alle Geräte</li>
            </ul>
          </div>
          <div className="highlight-box">
            <h4 className="font-bold mb-2 text-orange-600">📱 Geräte-spezifisch</h4>
            <ul>
              <li>• Windows: Netzwerkadapter</li>
              <li>• Android: WLAN-Einstellungen</li>
              <li>• iOS: WLAN-Details</li>
            </ul>
          </div>
        </div>
      </section>

      <section id="advanced-settings">
        <h2>Erweiterte Einstellungen</h2>
        <p>
          Pi-Hole bietet viele erweiterte Konfigurationsmöglichkeiten für optimale Performance.
        </p>

        <div className="highlight-box">
          <h3 className="font-bold mb-3 text-purple-600 flex items-center">
            <Settings size={20} style={{display:'inline',verticalAlign:'middle',marginRight:8}} />
            Web-Interface Konfiguration
          </h3>
          <p className="mb-3">
            Öffnen Sie das Admin-Panel unter: http://192.168.1.100/admin
          </p>
          <div className="grid-2">
            <div>
              <h4 className="font-semibold mb-2">Wichtige Einstellungen:</h4>
              <ul>
                <li>• Upstream DNS-Server</li>
                <li>• Conditional Forwarding</li>
                <li>• DNSSEC aktivieren</li>
                <li>• Rate Limiting</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Zusätzliche Blocklisten:</h4>
              <ul>
                <li>• StevenBlack's Listen</li>
                <li>• OISD Domain-Listen</li>
                <li>• Firebog Listen</li>
                <li>• Malware-Domain-Listen</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section id="maintenance">
        <h2>Wartung und Updates</h2>
        <p>
          Regelmäßige Wartung ist wichtig für optimale Performance und Sicherheit.
        </p>

        <div className="grid-2">
          <div className="highlight-box">
            <h4 className="font-bold mb-3 text-blue-600">🔄 Updates</h4>
            <div className="bg-white p-3 rounded border font-mono text-sm mb-3">
              <div>pihole -up</div>
              <div>apt update && apt upgrade -y</div>
            </div>
          </div>

          <div className="highlight-box">
            <h4 className="font-bold mb-3 text-orange-600">📊 Monitoring</h4>
            <ul>
              <li>• Anfrage-Geschwindigkeit</li>
              <li>• Blocked-Prozentsatz</li>
              <li>• System-Ressourcen</li>
              <li>• Log-Dateigröße</li>
            </ul>
          </div>
        </div>
      </section>

      <section id="troubleshooting">
        <h2>Troubleshooting</h2>
        <p>
          Häufige Probleme und deren Lösungen:
        </p>

        <div className="callout">
          <h4 className="font-bold mb-2 text-red-600">❌ Kein Internet</h4>
          <p><strong>Lösung:</strong> DNS-Einstellungen prüfen, Pi-Hole-Status checken</p>
        </div>

        <div className="callout">
          <h4 className="font-bold mb-2 text-yellow-600">⚠️ Langsame DNS-Auflösung</h4>
          <p><strong>Lösung:</strong> Upstream-DNS optimieren, Blocklisten reduzieren</p>
        </div>

        <div className="callout">
          <h4 className="font-bold mb-2 text-blue-600">🔧 Websites funktionieren nicht</h4>
          <p><strong>Lösung:</strong> Query-Log prüfen, Domains zur Whitelist hinzufügen</p>
        </div>

        <div className="highlight-box">
          <h4 className="font-bold mb-3">Debug-Befehle</h4>
          <div className="bg-white p-3 rounded border font-mono text-sm">
            <div>pihole status</div>
            <div>dig @127.0.0.1 google.com</div>
            <div>pihole -t</div>
          </div>
        </div>
      </section>

      <section id="conclusion">
        <h2>Fazit und weiterführende Tipps</h2>
        <p>
          Mit Pi-Hole auf Proxmox haben Sie eine mächtige und flexible Lösung für netzwerkweite Werbeblocker geschaffen. Die Container-basierte Installation bietet entscheidende Vorteile gegenüber Hardware-basierten Lösungen.
        </p>

        <div className="grid-2">
          <div className="highlight-box">
            <h4 className="font-bold mb-2 text-green-600">✅ Vorteile</h4>
            <ul>
              <li>• Einfache Backup/Restore</li>
              <li>• Ressourceneffizient</li>
              <li>• Zentrale Verwaltung</li>
              <li>• Hochverfügbarkeit</li>
              <li>• Snapshot-Funktionen</li>
            </ul>
          </div>
          <div className="highlight-box">
            <h4 className="font-bold mb-2 text-blue-600">🚀 Erweiterungen</h4>
            <ul>
              <li>• Multi-Instance Setup</li>
              <li>• Monitoring-Integration</li>
              <li>• Automatische Backups</li>
              <li>• Load Balancing</li>
              <li>• Geo-blocking</li>
            </ul>
          </div>
        </div>

        <div className="callout">
          <h3 className="font-bold mb-2">🎯 Nächste Schritte:</h3>
          <ol className="list-decimal ml-6">
            <li><strong>Monitoring einrichten:</strong> Grafana + InfluxDB für Statistiken</li>
            <li><strong>Redundanz schaffen:</strong> Zweite Pi-Hole-Instanz als Backup</li>
            <li><strong>Automatisierung:</strong> Scripts für Updates und Wartung</li>
            <li><strong>Sicherheit erhöhen:</strong> Firewall-Regeln implementieren</li>
            <li><strong>Performance optimieren:</strong> Caching und Tuning</li>
          </ol>
        </div>

        <p>
          Pi-Hole ist nur der Anfang für ein umfassendes Homelab-Setup. Die Kombination aus Proxmox und verschiedenen Containern/VMs ermöglicht es, eine professionelle IT-Infrastruktur im Heimbereich aufzubauen. Experimentieren Sie mit weiteren Services wie Reverse Proxies, Monitoring-Lösungen oder Home Automation Systemen.
        </p>
      </section>

    </BlogArticleLayout>
  );
}
